import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Star,
  CheckCircle, Clock, XCircle, FileText,
  Filter, X, Calendar
} from 'lucide-react';
import MultiSelectSearchFilter from '../content-management/common/MultiSelectSearchFilter';

const StudentProgressTab = ({ token, userRole }) => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const [hoveredSubmissions, setHoveredSubmissions] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const dropdownRef = useRef(null);
  const tooltipTimeoutRef = useRef(null);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [selectedNames, setSelectedNames] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterValues, setFilterValues] = useState({ names: [], emails: [], countries: [] });
  const statusDropdownRef = useRef(null);

  // Inline XP editing: { [studentId]: { value, saving } }
  const [xpInputs, setXpInputs] = useState({});

  const config = {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setGroupDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch data
  useEffect(() => {
    if (!token) return;
    fetchStudentProgress();
  }, [token]);

  const buildQueryString = useCallback((groupId, filters = {}) => {
    const params = new URLSearchParams();
    if (groupId) params.append('groupId', groupId);
    
    const s = filters.status || statusFilter;
    if (s && s !== 'all') params.append('status', s);
    
    const n = filters.names !== undefined ? filters.names : selectedNames;
    if (n.length > 0) params.append('names', n.join(','));
    
    const em = filters.emails !== undefined ? filters.emails : selectedEmails;
    if (em.length > 0) params.append('emails', em.join(','));
    
    const c = filters.countries !== undefined ? filters.countries : selectedCountries;
    if (c.length > 0) params.append('countries', c.join(','));
    
    const sd = filters.startDate !== undefined ? filters.startDate : startDate;
    if (sd) params.append('startDate', sd);
    
    const ed = filters.endDate !== undefined ? filters.endDate : endDate;
    if (ed) params.append('endDate', ed);

    const p = filters.page !== undefined ? filters.page : currentPage;
    if (p) params.append('page', p);
    params.append('limit', 50);
    
    return params.toString();
  }, [statusFilter, selectedNames, selectedEmails, selectedCountries, startDate, endDate, currentPage]);

  const fetchStudentProgress = async (groupId = null, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const qs = buildQueryString(groupId || selectedGroupId, filters);
      const url = `/api/users/student-progress${qs ? `?${qs}` : ''}`;
      const res = await axios.get(url, config);
      setGroups(res.data.groups || []);
      setStudents(res.data.students || []);
      setSelectedGroupId(res.data.selectedGroupId);
      if (res.data.pagination) {
        setPagination(res.data.pagination);
      }
      if (res.data.summary) {
        setSummary(res.data.summary);
      }
      if (res.data.filterValues) {
        setFilterValues(res.data.filterValues);
      }
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to load student progress');
    } finally {
      setLoading(false);
    }
  };

  const handleGroupSelect = (groupId) => {
    setSelectedGroupId(groupId);
    setGroupDropdownOpen(false);
    // Reset filters on group change
    setStatusFilter('all');
    setSelectedNames([]);
    setSelectedEmails([]);
    setSelectedCountries([]);
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    fetchStudentProgress(groupId, { status: 'all', names: [], emails: [], countries: [], startDate: '', endDate: '', page: 1 });
  };

  const handleFilterChange = (overrides = {}) => {
    setCurrentPage(1);
    fetchStudentProgress(selectedGroupId, { ...overrides, page: 1 });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchStudentProgress(selectedGroupId, { page: newPage });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearAllFilters = () => {
    setStatusFilter('all');
    setSelectedNames([]);
    setSelectedEmails([]);
    setSelectedCountries([]);
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    fetchStudentProgress(selectedGroupId, { status: 'all', names: [], emails: [], countries: [], startDate: '', endDate: '', page: 1 });
  };

  const hasActiveFilters = statusFilter !== 'all' || selectedNames.length > 0 || selectedEmails.length > 0 || 
    selectedCountries.length > 0 || startDate || endDate;

  const selectedGroup = groups.find(g => g._id === selectedGroupId);

  const statuses = [
    { value: 'all', label: 'All Students' },
    { value: 'active', label: 'Active Students' },
    { value: 'deactivated', label: 'Deactivated Students' }
  ];

  const getStatusBadge = (status) => {
    const styles = {
      approved: { bg: 'bg-green-100 text-green-700', icon: <CheckCircle size={12} /> },
      pending: { bg: 'bg-yellow-100 text-yellow-700', icon: <Clock size={12} /> },
      rejected: { bg: 'bg-red-100 text-red-700', icon: <XCircle size={12} /> }
    };
    const s = styles[status] || { bg: 'bg-gray-100 text-gray-700', icon: null };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.bg}`}>
        {s.icon}
        {status}
      </span>
    );
  };

  const handleSubmissionMouseEnter = (studentId, e) => {
    clearTimeout(tooltipTimeoutRef.current);
    // Only recalculate position if hovering a different student
    if (hoveredSubmissions !== studentId && e) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPos({ top: rect.top, right: window.innerWidth - rect.right });
    }
    setHoveredSubmissions(studentId);
  };

  const handleTooltipMouseEnter = () => {
    clearTimeout(tooltipTimeoutRef.current);
  };

  const handleSubmissionMouseLeave = () => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setHoveredSubmissions(null);
    }, 200);
  };

  const getXpValue = (student) => {
    if (xpInputs[student._id] !== undefined) return xpInputs[student._id].value;
    return student.xp?.totalXP ?? 0;
  };

  const setXpValue = (studentId, value) => {
    setXpInputs(prev => ({ ...prev, [studentId]: { ...prev[studentId], value } }));
  };

  const adjustXp = (student, delta) => {
    const current = getXpValue(student);
    const newValue = Math.max(0, Number(current) + delta);
    setXpValue(student._id, newValue);
    saveXpValue(student, newValue);
  };

  const saveXp = (student) => {
    const newValue = parseInt(getXpValue(student));
    saveXpValue(student, newValue);
  };

  const saveXpValue = async (student, newValue) => {
    const originalValue = student.xp?.totalXP ?? 0;
    if (isNaN(newValue) || newValue === originalValue) {
      setXpInputs(prev => { const n = { ...prev }; delete n[student._id]; return n; });
      return;
    }
    setXpInputs(prev => ({ ...prev, [student._id]: { value: newValue, saving: true } }));
    try {
      const res = await axios.patch(
        `/api/experiencepoints/adjust/${student._id}`,
        { amount: newValue },
        config
      );
      setStudents(prev => prev.map(s =>
        s._id === student._id
          ? { ...s, xp: { totalXP: res.data.xpProfile.totalXP, level: res.data.xpProfile.level } }
          : s
      ));
      setXpInputs(prev => { const n = { ...prev }; delete n[student._id]; return n; });
    } catch {
      setXpInputs(prev => { const n = { ...prev }; delete n[student._id]; return n; });
    }
  };

  if (loading && groups.length === 0) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-[#054581]">Student Progress Overview</h2>
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded w-64"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-[#054581]">Student Progress Overview</h2>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-2xl font-bold text-[#054581]">Student Progress Overview</h2>

        {/* Group Selector */}
        {groups.length > 0 && (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setGroupDropdownOpen(!groupDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg shadow-sm hover:border-[#1086a4] focus:outline-none focus:ring-2 focus:ring-[#1086a4]/20 focus:border-[#1086a4] transition-all min-w-[220px]"
            >
              <Users size={18} className="text-[#1086a4] flex-shrink-0" />
              <div className="flex-1 text-left">
                <div className="text-sm font-semibold text-[#1e2d40] truncate">
                  {selectedGroup?.title || 'Select Group'}
                </div>
                {selectedGroup?.program && (
                  <div className="text-xs text-gray-500 truncate">{selectedGroup.program}</div>
                )}
              </div>
              <ChevronDown 
                size={16} 
                className={`text-gray-400 transition-transform ${groupDropdownOpen ? 'rotate-180' : ''}`} 
              />
            </button>

            {groupDropdownOpen && (
              <div className="absolute right-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                {groups.map(group => (
                  <button
                    key={group._id}
                    onClick={() => handleGroupSelect(group._id)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 ${
                      group._id === selectedGroupId ? 'bg-[#1086a4]/5 border-l-2 border-l-[#1086a4]' : ''
                    }`}
                  >
                    <div className="font-medium text-[#1e2d40] text-sm">{group.title}</div>
                    <div className="text-xs text-gray-500">Program: {group.program || 'N/A'}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {groups.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
          <Users size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No groups available.</p>
        </div>
      ) : (
        <>
          {/* Filter Toggle & Header Row */}
          <div className="flex items-center justify-end">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                hasActiveFilters
                  ? 'bg-[#1086a4] text-white shadow-md'
                  : 'bg-white border border-[#1086a4] text-[#054581] hover:bg-[#f9fafb]'
              }`}
            >
              <Filter size={18} />
              <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
              {hasActiveFilters && (
                <span className="bg-white text-[#1086a4] text-xs px-2 py-0.5 rounded-full font-semibold">
                  Active
                </span>
              )}
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#054581]">Filter Students</h3>
                <button
                  onClick={clearAllFilters}
                  disabled={!hasActiveFilters}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    hasActiveFilters
                      ? 'bg-red-500 hover:bg-red-600 text-white cursor-pointer'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <X className="w-3 h-3" />
                  Clear All Filters
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Status Filter */}
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Filter className="w-4 h-4 text-[#1086a4]" />
                    <span className="text-sm font-medium text-[#054581]">Filter by Status</span>
                  </div>
                  <div className="relative" ref={statusDropdownRef}>
                    <label className="block text-xs text-gray-600 mb-1">Status</label>
                    <button
                      onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                      className="w-full inline-flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-lg hover:border-[#1086a4] transition-colors text-sm"
                    >
                      <span className="text-sm text-gray-700 truncate">
                        {statuses.find(s => s.value === statusFilter)?.label || 'All Students'}
                      </span>
                      <svg
                        className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ml-1 ${showStatusDropdown ? 'transform rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showStatusDropdown && (
                      <div className="absolute z-10 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200">
                        <div className="py-2">
                          {statuses.map((s) => (
                            <button
                              key={s.value}
                              onClick={() => {
                                setStatusFilter(s.value);
                                setShowStatusDropdown(false);
                                handleFilterChange({ status: s.value });
                              }}
                              className={`${
                                statusFilter === s.value
                                  ? 'bg-[#1086a4] text-white'
                                  : 'text-[#054581] hover:bg-[#f9fafb]'
                              } w-full flex items-center px-4 py-2 text-sm transition-colors`}
                            >
                              {statusFilter === s.value && (
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                              <span className={statusFilter !== s.value ? 'ml-6' : ''}>
                                {s.label}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Date Range Filter */}
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-[#1086a4]" />
                    <span className="text-sm font-medium text-[#054581]">Join Date Range</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">From</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          handleFilterChange({ startDate: e.target.value });
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">To</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          handleFilterChange({ endDate: e.target.value });
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Multi-select Filters */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <MultiSelectSearchFilter
                  items={filterValues.names.map(name => ({ _id: name, title: name }))}
                  selectedItems={selectedNames}
                  onSelectionChange={(selected) => {
                    setSelectedNames(selected);
                    handleFilterChange({ names: selected });
                  }}
                  title="Names"
                  placeholder="Search names..."
                  emptyMessage="No names available"
                />

                <MultiSelectSearchFilter
                  items={filterValues.emails.map(email => ({ _id: email, title: email }))}
                  selectedItems={selectedEmails}
                  onSelectionChange={(selected) => {
                    setSelectedEmails(selected);
                    handleFilterChange({ emails: selected });
                  }}
                  title="Emails"
                  placeholder="Search emails..."
                  emptyMessage="No emails available"
                />

                <MultiSelectSearchFilter
                  items={filterValues.countries.map(country => ({ _id: country, title: country }))}
                  selectedItems={selectedCountries}
                  onSelectionChange={(selected) => {
                    setSelectedCountries(selected);
                    handleFilterChange({ countries: selected });
                  }}
                  title="Countries"
                  placeholder="Search countries..."
                  emptyMessage="No countries available"
                />
              </div>
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
              <div className="text-2xl font-bold text-[#054581]">{summary?.totalStudents ?? students.length}</div>
              <div className="text-xs text-gray-500 mt-1">Total Students</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
              <div className="text-2xl font-bold text-green-600">
                {summary?.activeCount ?? students.filter(s => s.isActive).length}
              </div>
              <div className="text-xs text-gray-500 mt-1">Active</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
              <div className="text-2xl font-bold text-[#1086a4]">
                {summary?.pathCompletedCount ?? students.filter(s => s.isPathCompleted).length}
              </div>
              <div className="text-xs text-gray-500 mt-1">Path Completed</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {summary?.totalSubmissionCount ?? students.reduce((acc, s) => acc + s.submissionCount, 0)}
              </div>
              <div className="text-xs text-gray-500 mt-1">Total Submissions</div>
            </div>
          </div>

          {/* Student Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">Program</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">Group</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">XP</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Submissions</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                        <div className="animate-pulse space-y-2">
                          {[1,2,3].map(i => <div key={i} className="h-8 bg-gray-200 rounded"></div>)}
                        </div>
                      </td>
                    </tr>
                  ) : students.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                        No students found in this group.
                      </td>
                    </tr>
                  ) : (
                    students.map(student => (
                      <tr key={student._id} className="hover:bg-gray-50 transition-colors">
                        {/* Name */}
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#1e2d40] text-sm">
                            {student.name} {student.lastName}
                          </div>
                          <div className="text-xs text-gray-500 md:hidden">{student.email}</div>
                        </td>

                        {/* Email */}
                        <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">
                          {student.email}
                        </td>

                        {/* Program */}
                        <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">
                          {student.program}
                        </td>

                        {/* Group */}
                        <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">
                          {student.group}
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3 text-center">
                          {student.isActive ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                              Inactive
                            </span>
                          )}
                        </td>

                        {/* XP */}
                        <td className="px-4 py-3 text-center">
                          {userRole === 'admin' ? (
                            <div className="flex flex-col items-center gap-1">
                              <div className="flex items-center gap-1">
                                <button
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => adjustXp(student, -10)}
                                  className="w-5 h-5 rounded flex items-center justify-center text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors font-bold text-sm leading-none"
                                  title="−10 XP"
                                >−</button>
                                <input
                                  type="number"
                                  min="0"
                                  value={getXpValue(student)}
                                  onChange={(e) => setXpValue(student._id, String(Math.max(0, parseInt(e.target.value) || 0)))}
                                  onKeyDown={(e) => e.key === 'Enter' && saveXp(student)}
                                  onBlur={() => saveXp(student)}
                                  disabled={xpInputs[student._id]?.saving}
                                  className="w-16 text-center text-sm font-semibold text-[#1e2d40] border border-gray-200 rounded-md px-1 py-0.5 focus:outline-none focus:border-[#1086a4] focus:ring-1 focus:ring-[#1086a4] disabled:opacity-50"
                                />
                                <button
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => adjustXp(student, 10)}
                                  className="w-5 h-5 rounded flex items-center justify-center text-green-500 hover:bg-green-50 hover:text-green-700 transition-colors font-bold text-sm leading-none"
                                  title="+10 XP"
                                >+</button>
                              </div>
                              <div className="text-xs text-gray-400">
                                {xpInputs[student._id]?.saving ? 'Saving…' : `Lvl ${student.xp?.level || 1}`}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="flex items-center justify-center gap-1">
                                <Star size={14} className="text-yellow-500" />
                                <span className="text-sm font-semibold text-[#1e2d40]">
                                  {student.xp?.totalXP?.toLocaleString() || 0}
                                </span>
                              </div>
                              <div className="text-xs text-gray-400">Lvl {student.xp?.level || 1}</div>
                            </div>
                          )}
                        </td>

                        {/* Submissions with hover tooltip */}
                        <td className="px-4 py-3 text-center">
                          <div
                            className="inline-flex items-center gap-1 cursor-pointer"
                            onMouseEnter={(e) => handleSubmissionMouseEnter(student._id, e)}
                            onMouseLeave={handleSubmissionMouseLeave}
                          >
                            <FileText size={14} className="text-[#1086a4]" />
                            <span className="text-sm font-semibold text-[#1e2d40]">
                              {student.submissionCount}
                            </span>
                          </div>
                        </td>

                        {/* View Profile */}
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => navigate(`/profile?id=${student._id}`)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#1086a4] bg-[#1086a4]/5 rounded-lg hover:bg-[#1086a4]/10 hover:text-[#054581] transition-colors"
                          >
                            <ExternalLink size={14} />
                            View Profile
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} students
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!pagination.hasPrevPage}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      pagination.hasPrevPage
                        ? 'text-[#054581] bg-gray-100 hover:bg-gray-200'
                        : 'text-gray-400 bg-gray-50 cursor-not-allowed'
                    }`}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      pagination.hasNextPage
                        ? 'text-[#054581] bg-gray-100 hover:bg-gray-200'
                        : 'text-gray-400 bg-gray-50 cursor-not-allowed'
                    }`}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Fixed-position Submissions Tooltip - rendered outside table to avoid overflow clipping */}
      {hoveredSubmissions && tooltipPos && (() => {
        const student = students.find(s => s._id === hoveredSubmissions);
        if (!student || student.submissions.length === 0) return null;
        return (
          <div
            className="fixed z-[9999] w-72 bg-white border border-gray-200 rounded-lg shadow-xl p-3"
            style={{ top: tooltipPos.top - 4, right: tooltipPos.right, transform: 'translateY(-100%)' }}
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleSubmissionMouseLeave}
          >
            <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
              Submissions ({student.submissions.length})
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1.5">
              {student.submissions.map((sub, i) => (
                <div key={sub._id || i} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1.5">
                  <span className="text-gray-700 truncate mr-2 flex-1">{sub.formTitle}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {getStatusBadge(sub.status)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const mapStateToProps = state => ({
  token: state.auth.token,
  userRole: state.auth.user?.role
});

export default connect(mapStateToProps)(StudentProgressTab);
